import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  EyeSlashIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Portfolio = () => {
  const [showBalance, setShowBalance] = useState(true);
  const [timeframe, setTimeframe] = useState('1M');

  const portfolioValue = 125680.45;
  const todayChange = 2847.32;
  const todayChangePercent = 2.31;

  const holdings = [
    { symbol: 'AAPL', name: 'Apple Inc.', quantity: 150, value: 26767.50, allocation: 21.3, change: 345.67 },
    { symbol: 'GOOGL', name: 'Alphabet Inc.', quantity: 8, value: 21876.48, allocation: 17.4, change: -234.56 },
    { symbol: 'BTC', name: 'Bitcoin', quantity: 0.75, value: 34259.18, allocation: 27.3, change: 1234.89 },
    { symbol: 'ETH', name: 'Ethereum', quantity: 12, value: 38813.72, allocation: 30.9, change: 567.23 },
    { symbol: 'TSLA', name: 'Tesla Inc.', quantity: 45, value: 10560.15, allocation: 8.4, change: 123.45 },
  ];

  const performanceData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{
      label: 'Portfolio Value',
      data: [95000, 98000, 102000, 115000, 118000, 125680],
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      fill: true,
      tension: 0.4,
    }],
  };

  const allocationData = {
    labels: holdings.map(h => h.symbol),
    datasets: [{
      data: holdings.map(h => h.allocation),
      backgroundColor: [
        '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'
      ],
      borderWidth: 0,
    }],
  };

  return (
          <div className="min-h-screen bg-gray-50 w-full overflow-x-hidden">
        <div className="mx-auto max-w-7xl px-2 sm:px-4 lg:px-8 pt-20 pb-8">
        {/* Portfolio Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">Portfolio</h1>
            <button
              onClick={() => setShowBalance(!showBalance)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              {showBalance ? <EyeSlashIcon className="h-5 w-5 mr-2" /> : <EyeIcon className="h-5 w-5 mr-2" />}
              {showBalance ? 'Hide' : 'Show'} Balance
            </button>
          </div>
        </div>

        {/* Portfolio Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card"
          >
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-8 w-8 text-primary-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Total Portfolio Value</p>
                <p className="text-2xl font-bold text-gray-900">
                  {showBalance ? `$${portfolioValue.toLocaleString()}` : '••••••'}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card"
          >
            <div className="flex items-center">
              <ArrowTrendingUpIcon className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Today's P&L</p>
                <div className="flex items-center">
                  <p className="text-2xl font-bold text-green-600">
                    {showBalance ? `+$${todayChange.toLocaleString()}` : '••••••'}
                  </p>
                  <ArrowUpIcon className="h-5 w-5 text-green-600 ml-2" />
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card"
          >
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-primary-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Total Return</p>
                <p className="text-2xl font-bold text-green-600">
                  {showBalance ? `+${todayChangePercent}%` : '••••'}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Performance Chart */}
          <div className="lg:col-span-2 card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Portfolio Performance</h2>
              <div className="flex space-x-2">
                {['1W', '1M', '3M', '6M', '1Y'].map((period) => (
                  <button
                    key={period}
                    onClick={() => setTimeframe(period)}
                    className={`px-3 py-1 rounded text-sm ${
                      timeframe === period
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {period}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-64">
              <Line
                data={performanceData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    y: { beginAtZero: false },
                    x: { display: true }
                  }
                }}
              />
            </div>
          </div>

          {/* Asset Allocation */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-6">Asset Allocation</h2>
            <div className="h-64 flex items-center justify-center">
              <Doughnut
                data={allocationData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'bottom' }
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Holdings Table */}
        <div className="mt-8 card">
          <h2 className="text-xl font-semibold mb-6">Holdings</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 text-sm font-medium text-gray-500">Asset</th>
                  <th className="text-left py-3 text-sm font-medium text-gray-500">Quantity</th>
                  <th className="text-left py-3 text-sm font-medium text-gray-500">Value</th>
                  <th className="text-left py-3 text-sm font-medium text-gray-500">Allocation</th>
                  <th className="text-left py-3 text-sm font-medium text-gray-500">Today's Change</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((holding, index) => (
                  <motion.tr
                    key={holding.symbol}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border-b border-gray-100"
                  >
                    <td className="py-4">
                      <div>
                        <div className="font-medium text-gray-900">{holding.symbol}</div>
                        <div className="text-sm text-gray-500">{holding.name}</div>
                      </div>
                    </td>
                    <td className="py-4 text-gray-900">{holding.quantity}</td>
                    <td className="py-4 font-medium text-gray-900">
                      {showBalance ? `$${holding.value.toLocaleString()}` : '••••••'}
                    </td>
                    <td className="py-4 text-gray-900">{holding.allocation}%</td>
                    <td className="py-4">
                      <div className={`flex items-center ${holding.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {holding.change >= 0 ? <ArrowUpIcon className="h-4 w-4 mr-1" /> : <ArrowDownIcon className="h-4 w-4 mr-1" />}
                        {showBalance ? `$${Math.abs(holding.change).toLocaleString()}` : '••••'}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Portfolio; 